import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";
import { rtlConfirmMessage, rtlGate } from "@/lib/rtlSafety";
import { timeAgo } from "@/lib/format";
import { disarmGate, armAutoDisarmRemaining, isTelemetryStale,
  prepareGate, armGate, takeoffGate, isResultUnknown, stepUnlocked, identityNewFlightBlocked } from "@/lib/commandGating";
import {
  Power, PowerOff, ArrowUp, ArrowDown, Home, ShieldCheck, ShieldAlert,
  Radio, Wifi, Satellite, BatteryMedium, Gauge as GaugeIcon, Cpu, Clock,
  CheckCircle2, XCircle, Loader2, AlertTriangle,
} from "lucide-react";

// Bornes TAKEOFF mode test (le backend fait foi et re-valide ; ici pour l'UI).
const TAKEOFF_MIN = 1;
const TAKEOFF_MAX = 3;

// Étapes affichées du cycle de vie réel.
const STAGES = [
  { key: "sent", label: "Envoyée" },
  { key: "received", label: "Reçue par le drone" },
  { key: "px4_accepted", label: "Acceptée PX4" },
  { key: "executed", label: "Exécutée" },
];
const ERROR_STATUSES = ["failed", "timeout", "expired", "precheck_failed", "px4_rejected"];

function stageIndex(status) {
  if (["created", "sent"].includes(status)) return 0;
  if (["received", "precheck_passed", "sent_to_px4"].includes(status)) return 1;
  if (["px4_accepted", "executing"].includes(status)) return 2;
  if (status === "executed") return 3;
  return -1;
}

const STATUS_LABEL = {
  created: "Créée", sent: "Envoyée", received: "Reçue", precheck_passed: "Prechecks OK",
  precheck_failed: "Prechecks échoués", sent_to_px4: "Transmise PX4", px4_accepted: "Acceptée PX4",
  px4_rejected: "Refusée PX4", executing: "En exécution", executed: "Exécutée",
  failed: "Échec", timeout: "Timeout", expired: "Expirée", result_unknown: "Résultat incertain",
};

export default function CommandCenter({ drone, droneId, compact = false }) {
  const [confirm, setConfirm] = useState(null);
  const [takeoffOpen, setTakeoffOpen] = useState(false);
  const [armStep, setArmStep] = useState(0); // double confirmation ARM
  const [prepared, setPrepared] = useState(false); // MATRIX.1 : prepare_takeoff 'executed' -> ARM débloqué
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null); // commande en cours de suivi
  const [journal, setJournal] = useState([]);
  const [armCountdown, setArmCountdown] = useState(null); // COM_DISARM_PRFLT=10 : fenêtre ARM->TAKEOFF
  const [nowMs, setNowMs] = useState(Date.now()); // tick 1s pour la fraîcheur télémétrie
  const pollRef = useRef(null);
  const armTimerRef = useRef(null);

  // Disponibilité = LIAISON (WS drone + MAVLink/heartbeat FRAIS), PAS la télémétrie GPS ("online").
  // GATING STRICT LOT 1 : mavlink_connected doit être STRICTEMENT === true ET la télémétrie fraîche.
  // Absent / null / stale => liaison NON fiable => commandes bloquées (aucun fallback tolérant).
  const wsOk = !!drone?.drone_ws_connected;
  const stale = drone?.telemetry_stale === true || isTelemetryStale(drone?.last_seen, nowMs);
  const mavConnected = drone?.mavlink_connected === true;
  const mavOk = mavConnected && !stale;
  const linkReady = wsOk && mavOk;
  const gps = !!drone?.gps_fix;
  const rtlG = rtlGate(drone, nowMs);
  const bat = drone?.battery;
  const batOk = bat == null || bat > 15; // BAT_LOW_THR=0.15
  const armed = !!drone?.armed;
  // Altitude cockpit = hauteur opérationnelle (relative_alt) — JAMAIS l'AMSL.
  const relAlt = drone?.relative_alt != null ? Number(drone.relative_alt) : null;
  const disarm = disarmGate({ linkReady, busy, armed, landedState: drone?.landed_state });
  const realEnabled = !!drone?.real_commands_enabled;
  // result_unknown : incertitude réconciliable (ni succès, ni échec, ni timeout). Verrouille la suite.
  const unknownActive = isResultUnknown(active?.status);
  // MATRIX.1 — séquence imposée PREPARE -> ARM -> TAKEOFF (gates PURS, testables hors React).
  // Aucun flux physique direct ARM -> TAKEOFF : l'ARM exige un PREPARE 'executed', le TAKEOFF
  // exige PREPARE 'executed' + télémétrie armed=true. MATRIX.1.1 : `uncertain` verrouille tout
  // tant qu'un result_unknown n'est pas réconcilié (jamais de relance, même ARM result_unknown
  // avec armed=true ne débloque PAS le TAKEOFF).
  // MATRIX.5 — gate d'identité contrôleur (garde UX). Bloque un NOUVEAU démarrage de vol
  // (PREPARE/ARM/TAKEOFF) si identity_ready !== true. LAND/RTL/DISARM ne sont JAMAIS gatés ici.
  const identityBlock = identityNewFlightBlocked(drone?.flight_controller);
  const prepare = prepareGate({ linkReady, busy, armed, gps, batOk, prepared, uncertain: unknownActive, identityBlock });
  const arm = armGate({ linkReady, busy, armed, prepared, uncertain: unknownActive, identityBlock });
  const takeoff = takeoffGate({ linkReady, busy, armed, gps, batOk, prepared, uncertain: unknownActive, identityBlock });
  const takeoffDisabled = takeoff.disabled;
  const takeoffReason = takeoff.reason || null;
  // Libellé MAVLink : OFF (false) / STALE (true mais périmé) / INCONNU (null) / OK.
  const mavLabel = drone?.mavlink_connected == null ? "INCONNU"
    : (!mavConnected ? "OFF" : (stale ? "STALE" : "OK"));

  const loadJournal = useCallback(async () => {
    try {
      const { data } = await api.get(`/drones/${droneId}/commands`, { params: { limit: 12 } });
      setJournal(data.commands || []);
    } catch { /* silencieux */ }
  }, [droneId]);

  useEffect(() => { loadJournal(); }, [loadJournal]);
  useEffect(() => () => { clearInterval(pollRef.current); clearInterval(armTimerRef.current); }, []);
  // Tick 1 s : réévalue la fraîcheur télémétrie (gating strict) même sans nouvelle donnée.
  useEffect(() => { const t = setInterval(() => setNowMs(Date.now()), 1000); return () => clearInterval(t); }, []);

  // Compte à rebours d'AUTO-DÉSARMEMENT PX4 (COM_DISARM_PRFLT=10 s) après un ARM au sol.
  // Le point de départ est calé sur executed_at réel (et non sur l'instant où l'UI reçoit l'ACK).
  const startArmCountdown = useCallback((executedAt) => {
    clearInterval(armTimerRef.current);
    const compute = () => armAutoDisarmRemaining(executedAt);
    setArmCountdown(compute());
    armTimerRef.current = setInterval(() => {
      const rem = compute();
      setArmCountdown(rem);
      if (rem <= 0) clearInterval(armTimerRef.current);
    }, 1000);
  }, []);

  useEffect(() => {
    if (active?.command === "arm" && active?.status === "executed") startArmCountdown(active?.executed_at);
  }, [active?.command, active?.status, active?.executed_at, startArmCountdown]);

  // TAKEOFF lancé -> on annule le compte à rebours.
  useEffect(() => {
    if (active?.command === "takeoff") { clearInterval(armTimerRef.current); setArmCountdown(null); }
  }, [active?.command]);

  // PX4 a auto-désarmé (télémétrie armed=false) pendant le compte à rebours -> retour propre à DISARMED.
  useEffect(() => {
    if (armCountdown != null && drone?.armed === false) {
      clearInterval(armTimerRef.current);
      setArmCountdown(null);
      toast.info("DÉSARMÉ — PX4 a auto-désarmé (fenêtre de décollage dépassée)");
    }
  }, [drone?.armed, armCountdown]);

  // MATRIX.1 — PREPARE validé (status 'executed', JAMAIS result_unknown) -> débloque l'ARM.
  useEffect(() => {
    if (active?.command === "prepare_takeoff" && stepUnlocked(active?.status)) setPrepared(true);
  }, [active?.command, active?.status]);

  // TAKEOFF terminé -> la préparation est consommée (séquence à refaire pour un nouveau vol).
  useEffect(() => {
    if (active?.command === "takeoff" && stepUnlocked(active?.status)) setPrepared(false);
  }, [active?.command, active?.status]);

  // Lien MAVLink perdu -> on invalide la préparation (la séquence doit repartir de PREPARE).
  useEffect(() => { if (!linkReady) setPrepared(false); }, [linkReady]);

  const pollCommand = (cid) => {
    clearInterval(pollRef.current);
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      ticks += 1;
      try {
        const { data } = await api.get(`/drones/${droneId}/commands/${cid}`);
        setActive(data);
        // MATRIX.1.1 — result_unknown est NON terminal et réconciliable : on NE stoppe PAS le GET
        // read-only juste parce que ticks>30. On poursuit jusqu'à un statut terminal/autoritaire
        // ou le démontage du composant. Aucun nouveau POST n'est émis.
        const uncertain = data.status === "result_unknown";
        if (data.terminal || (ticks > 30 && !uncertain)) {
          clearInterval(pollRef.current);
          loadJournal();
        }
      } catch {
        if (ticks > 30) clearInterval(pollRef.current);
      }
    }, 1000);
  };

  const run = async (label, fn) => {
    setBusy(true);
    try {
      const { data } = await fn();
      setActive(data);
      toast.success(`${label} — ${STATUS_LABEL[data.status] || data.status}`, {
        description: `command_id: ${data.command_id?.slice(0, 8)}…`,
      });
      pollCommand(data.command_id);
      loadJournal();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      toast.error(`${label} refusée`, { description: code ? `${code} — ${apiErr(e)}` : apiErr(e) });
    } finally {
      setBusy(false);
      setConfirm(null);
      setTakeoffOpen(false);
      setArmStep(0);
    }
  };

  const cmd = (action, params) => () => api.post(`/drones/${droneId}/${action}`, null, params ? { params } : undefined);

  return (
    <div className={compact ? "space-y-2.5" : "space-y-3"} data-testid="command-center">
      {/* Bandeau réel/simulé */}
      <div data-testid="real-commands-badge"
        className={`flex items-center gap-2 rounded-sm px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-wider border ${
          realEnabled ? "border-[#F59E0B]/50 bg-[#F59E0B]/10 text-[#F59E0B]" : "border-white/15 bg-white/[0.03] text-[#A3A3A3]"
        }`}>
        {realEnabled ? <ShieldAlert size={13} /> : <ShieldCheck size={13} />}
        {realEnabled ? "Commandes de vol : RÉELLES — MODE TEST" : "Commandes de vol : SIMULÉES / DÉSACTIVÉES"}
      </div>

      {identityBlock && (
        <div data-testid="identity-block-banner"
          className="font-mono text-[10px] text-[#EF4444] border border-[#EF4444]/40 bg-[#EF4444]/10 rounded-sm px-2 py-1.5">
          {identityBlock}
        </div>
      )}
      {/* MATRIX.1 — indicateur de séquence PREPARE -> ARM -> TAKEOFF */}
      <div data-testid="flight-sequence" className="flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-wider">
        <SeqStep label="Prepare" done={prepared} active={!prepared} />
        <span className="text-[#525252]">→</span>
        <SeqStep label="Arm" done={armed} active={prepared && !armed} />
        <span className="text-[#525252]">→</span>
        <SeqStep label="Takeoff" done={false} active={prepared && armed} />
      </div>

      {/* État live du drone */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5" data-testid="live-state">
        <StateChip icon={Radio} label="MAVLink" ok={mavConnected && !stale} warn={mavConnected && stale} value={mavLabel} />
        <StateChip icon={Wifi} label="WS drone" ok={drone?.drone_ws_connected} value={drone?.drone_ws_connected ? "OK" : "OFF"} />
        <StateChip icon={Power} label="Armé" ok={!armed} warn={armed} value={armed ? "OUI" : "NON"} />
        <StateChip icon={Cpu} label="Mode" value={drone?.mode || "—"} />
        <StateChip icon={Satellite} label="GPS" ok={drone?.gps_fix} value={`${drone?.gps_fix ? "FIX" : "NON"}${drone?.sats != null ? " · " + drone.sats + " sat" : ""}`} />
        <StateChip icon={BatteryMedium} label="Batterie" ok={(drone?.battery || 0) >= 30} warn={(drone?.battery || 0) < 30} value={drone?.battery != null ? `${Math.round(drone.battery)}%` : "—"} />
        <StateChip icon={GaugeIcon} label="Altitude (rel.)" value={relAlt != null ? `${relAlt.toFixed(1)} m` : "—"} />
        <StateChip icon={Clock} label="Télémétrie" ok={!stale} warn={stale} value={drone?.last_seen ? timeAgo(drone.last_seen) : "—"} />
      </div>

      {/* Checklist « Prêt au vol » — synthèse go/no-go (mêmes états autoritaires que le backend/drone) */}
      <div data-testid="ready-to-fly" className="rounded-sm border border-white/10 bg-white/[0.02] p-2.5">
        <div className="font-chivo font-bold uppercase tracking-wider text-[11px] text-[#A3A3A3] mb-2">Prêt au vol</div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-wider">
          <Check label="MAVLink" ok={mavConnected && !stale} value={mavLabel} />
          <Check label="GPS fix" ok={gps} value={`${gps ? "OUI" : "NON"}${drone?.sats != null ? ` · ${drone.sats} sat` : ""}`} />
          <Check label="Batterie" ok={(drone?.battery ?? 0) >= 30} value={drone?.battery != null ? `${Math.round(drone.battery)}%` : "—"} />
          <Check label="Armé" ok={armed} neutral value={armed ? "OUI" : "NON"} />
          <Check label="Position" ok={gps} value={gps ? "OK" : "—"} />
          <Check label="Altitude rel." ok={relAlt != null} value={relAlt != null ? `${relAlt.toFixed(1)} m` : "—"} />
          <Check label="TAKEOFF" ok={!takeoffDisabled} value={takeoffDisabled ? "BLOQUÉ" : "PRÊT"} />
          <Check label="Liaison" ok={linkReady} value={linkReady ? "PRÊTE" : "REQUISE"} />
        </div>
      </div>

      {/* Bannière disponibilité liaison (gating strict MAVLink) */}
      {!linkReady && (
        <div data-testid="link-required" className="flex items-center gap-2 rounded-sm px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-wider border border-[#EF4444]/40 bg-[#EF4444]/10 text-[#EF4444]">
          <AlertTriangle size={13} /> Liaison requise :{!wsOk ? " WS drone déconnecté" : ""}{drone?.mavlink_connected == null ? " · MAVLink inconnu" : (!mavConnected ? " · MAVLink OFF" : "")}{mavConnected && stale ? " · télémétrie périmée" : ""}
        </div>
      )}

      {/* Compte à rebours ARM -> TAKEOFF (COM_DISARM_PRFLT) */}
      {armCountdown != null && (
        <div data-testid="arm-countdown"
          className={`flex items-center gap-2 rounded-sm px-2.5 py-2 font-mono text-xs uppercase tracking-wider border ${
            armCountdown > 0 ? "border-[#F59E0B]/50 bg-[#F59E0B]/10 text-[#F59E0B]" : "border-[#EF4444]/50 bg-[#EF4444]/10 text-[#EF4444]"
          }`}>
          <Power size={14} />
          {armCountdown > 0
            ? <span data-testid="arm-countdown-value">ARMÉ AU SOL — auto-désarmement PX4 dans {armCountdown} s</span>
            : <span data-testid="arm-countdown-value">Auto-désarmement PX4 imminent — ré-armer si besoin</span>}
        </div>
      )}

      {/* result_unknown : résultat incertain (réconciliable) — ni succès, ni échec, ni timeout */}
      {unknownActive && (
        <div data-testid="result-unknown-banner"
          className="flex items-start gap-2 rounded-sm px-2.5 py-2 font-mono text-[10px] uppercase tracking-wider border border-[#F59E0B]/50 bg-[#F59E0B]/10 text-[#F59E0B]">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span data-testid="result-unknown-text">Résultat incertain ({active?.command?.toUpperCase()}) — réconciliation en cours, suivi en lecture seule. Aucune relance automatique ; l'étape suivante reste verrouillée.</span>
        </div>
      )}

      {/* Cycle de vie de la commande active */}
      {active && <Lifecycle cmd={active} />}

      {/* Boutons de commande — séquence MATRIX.1 : PREPARE -> ARM -> TAKEOFF */}
      <div className="grid grid-cols-2 gap-2">
        <CmdBtn testid="btn-prepare" icon={ShieldCheck} label="PREPARE" disabled={prepare.disabled}
          title={prepare.reason}
          onClick={() => setConfirm({ title: "Préparation TAKEOFF", message: "Valider la préparation (vérifications d'état + capture de la référence de lancement). Aucune action physique, aucun ARM.", confirmLabel: "PRÉPARER",
            action: () => run("PREPARE", cmd("prepare_takeoff")) })} />
        <CmdBtn testid="btn-arm" icon={Power} label="ARM" disabled={arm.disabled}
          title={arm.reason}
          onClick={() => setArmStep(1)} />
        <CmdBtn testid="btn-takeoff" icon={ArrowUp} label="TAKEOFF" disabled={takeoff.disabled}
          title={takeoff.reason}
          onClick={() => setTakeoffOpen(true)} />
        <CmdBtn testid="btn-land" icon={ArrowDown} label="LAND" disabled={!linkReady || busy || !armed}
          title={!linkReady ? "Liaison requise" : !armed ? "Drone non armé / pas en vol" : ""}
          onClick={() => setConfirm({ title: "Atterrissage", message: "Confirmer l'atterrissage immédiat ?", confirmLabel: "LAND",
            action: () => { setPrepared(false); run("LAND", cmd("land")); } })} />
        <CmdBtn testid="btn-disarm" icon={PowerOff} label="DISARM" subtle disabled={disarm.disabled}
          title={disarm.reason}
          onClick={() => setConfirm({ title: "Désarmement", message: "Le drone est-il au sol ? Confirmer le désarmement ?", danger: true, confirmLabel: "DISARM",
            action: () => { setPrepared(false); run("DISARM", cmd("disarm")); } })} />
      </div>

      {/* Raison de blocage TAKEOFF affichée en clair (répond au « pourquoi désactivé ? ») */}
      {linkReady && takeoffDisabled && takeoffReason && (
        <div data-testid="takeoff-blocked-reason" className="flex items-center gap-2 rounded-sm px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-wider border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]">
          <AlertTriangle size={13} /> TAKEOFF indisponible : {takeoffReason}
        </div>
      )}

      <button data-testid="btn-rtl-emergency" disabled={busy || !linkReady || !gps || !rtlG.allowed}
        title={rtlG.reason || (!linkReady ? "Liaison requise" : !gps ? "GPS + Home requis pour RTL" : "")}
        onClick={() => setConfirm({ title: "RTL — Retour au point Home", message: rtlConfirmMessage(drone), danger: true, confirmLabel: "RTL",
          action: () => { setPrepared(false); run("RTL", cmd("rtl")); } })}
        className="w-full flex items-center justify-center gap-2 bg-transparent border-2 border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white rounded-sm font-black font-chivo tracking-widest uppercase py-3 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#EF4444]"
        style={{ boxShadow: "0 0 20px rgba(239,68,68,0.15)" }}>
        <Home size={18} /> RTL
      </button>
      {!rtlG.allowed && (
        <div data-testid="rtl-blocked-reason" className="font-mono text-[10px] uppercase tracking-wider text-[#A3A3A3]">
          RTL indisponible : {rtlG.reason}
        </div>
      )}

      {/* Journal des commandes */}
      {!compact && <CommandJournal items={journal} />}

      {/* Confirmations simples RTL/LAND/DISARM */}
      <ConfirmDialog open={!!confirm} title={confirm?.title} message={confirm?.message}
        confirmLabel={confirm?.confirmLabel} danger={confirm?.danger}
        onCancel={() => setConfirm(null)} onConfirm={confirm?.action} testid="command-confirm" />

      {/* ARM — double confirmation renforcée */}
      {armStep === 1 && (
        <ConfirmDialog open title="Armer DRONE-V1 ?" danger confirmLabel="Continuer"
          message="Les moteurs peuvent démarrer. Confirmation 1/2."
          onCancel={() => setArmStep(0)} onConfirm={() => setArmStep(2)} testid="arm-confirm-1" />
      )}
      {armStep === 2 && (
        <ConfirmDialog open title="Confirmer l'armement" danger confirmLabel="ARMER"
          message="Dernière confirmation (2/2). Les moteurs vont être armés."
          onCancel={() => setArmStep(0)} onConfirm={() => run("ARM", cmd("arm"))} testid="arm-confirm-2" />
      )}

      {/* TAKEOFF — confirmation renforcée avec altitude bornée */}
      {takeoffOpen && (
        <TakeoffDialog onCancel={() => setTakeoffOpen(false)}
          onConfirm={(a) => run("TAKEOFF", cmd("takeoff", { altitude: a }))} />
      )}
    </div>
  );
}

function StateChip({ icon: Icon, label, value, ok, warn }) {
  const color = warn ? "#F59E0B" : ok ? "#22C55E" : ok === false ? "#EF4444" : "#A3A3A3";
  return (
    <div className="flex items-center gap-1.5 border border-white/10 bg-white/[0.02] rounded-sm px-2 py-1.5">
      <Icon size={12} style={{ color }} />
      <div className="min-w-0">
        <div className="font-mono text-[8px] uppercase tracking-wider text-[#666]">{label}</div>
        <div className="font-mono text-[10px] truncate" style={{ color }}>{value}</div>
      </div>
    </div>
  );
}

function Lifecycle({ cmd }) {
  const unknown = cmd.status === "result_unknown";
  const err = !unknown && ERROR_STATUSES.includes(cmd.status);
  const idx = stageIndex(cmd.status);
  const headColor = err ? "#EF4444" : unknown ? "#F59E0B" : cmd.status === "executed" ? "#22C55E" : "#3B82F6";
  return (
    <div data-testid={`command-lifecycle-${cmd.command_id}`}
      className={`rounded-sm border px-3 py-2.5 ${err ? "border-[#EF4444]/40 bg-[#EF4444]/5" : unknown ? "border-[#F59E0B]/40 bg-[#F59E0B]/5" : "border-[#3B82F6]/30 bg-[#3B82F6]/5"}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-chivo font-bold uppercase text-sm">{cmd.command}</span>
        <span data-testid="lifecycle-status" className="font-mono text-[10px] uppercase tracking-wider flex items-center gap-1"
          style={{ color: headColor }}>
          {err ? <XCircle size={12} /> : unknown ? <AlertTriangle size={12} /> : cmd.status === "executed" ? <CheckCircle2 size={12} /> : <Loader2 size={12} className="animate-spin" />}
          {STATUS_LABEL[cmd.status] || cmd.status}
        </span>
      </div>
      {unknown ? (
        <div className="flex items-start gap-1.5 font-mono text-[10px] text-[#F59E0B]">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <span>Résultat indéterminé — réconciliable par un ACK tardif. Suivi en lecture seule, aucune relance automatique.</span>
        </div>
      ) : err ? (
        <div className="flex items-start gap-1.5 font-mono text-[10px] text-[#EF4444]">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <span>{cmd.error?.message || cmd.message || "Commande non exécutée"}</span>
        </div>
      ) : (
        <div className="flex items-center gap-1">
          {STAGES.map((s, i) => (
            <div key={s.key} className="flex-1 flex items-center gap-1">
              <div className="flex flex-col items-center gap-1 flex-1">
                <div className={`w-2 h-2 rounded-full ${i <= idx ? "bg-[#22C55E]" : "bg-white/15"}`} />
                <span className={`font-mono text-[7px] uppercase text-center leading-tight ${i <= idx ? "text-white" : "text-[#555]"}`}>{s.label}</span>
              </div>
              {i < STAGES.length - 1 && <div className={`h-px flex-1 ${i < idx ? "bg-[#22C55E]" : "bg-white/15"}`} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SeqStep({ label, done, active }) {
  const color = done ? "#22C55E" : active ? "#F59E0B" : "#525252";
  return (
    <span data-testid={`seq-${label.toLowerCase()}`} className="flex items-center gap-1" style={{ color }}>
      {done ? <CheckCircle2 size={11} /> : <span className="w-2 h-2 rounded-full" style={{ background: color }} />}
      {label}
    </span>
  );
}

function CommandJournal({ items }) {
  if (!items?.length) return null;
  return (
    <div data-testid="command-journal" className="border border-white/10 rounded-sm">
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3] px-2.5 py-1.5 border-b border-white/10">Journal des commandes</div>
      <div className="max-h-40 overflow-y-auto divide-y divide-white/5">
        {items.map((c) => {
          const err = ERROR_STATUSES.includes(c.status);
          return (
            <div key={c.command_id} className="flex items-center justify-between px-2.5 py-1.5 font-mono text-[10px]">
              <span className="text-[#A3A3A3]">{(c.created_at || "").slice(11, 19)} · <span className="text-white">{c.command?.toUpperCase()}</span></span>
              <span style={{ color: err ? "#EF4444" : c.status === "executed" ? "#22C55E" : "#F59E0B" }}>{STATUS_LABEL[c.status] || c.status}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Check({ label, ok, value, neutral }) {
  const color = neutral ? "#A3A3A3" : ok ? "#22C55E" : "#EF4444";
  return (
    <div className="flex items-center justify-between gap-2" data-testid={`check-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <span className="text-[#737373]">{label}</span>
      <span style={{ color }}>{value}</span>
    </div>
  );
}

function CmdBtn({ icon: Icon, label, onClick, disabled, testid, subtle, title }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled} title={title}
      className={`flex items-center justify-center gap-2 border rounded-sm font-mono tracking-wider uppercase text-xs py-2.5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
        subtle ? "border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#F59E0B]/10" : "border-white/20 text-white hover:bg-white/10"
      }`}>
      <Icon size={14} /> {label}
    </button>
  );
}

function TakeoffDialog({ onConfirm, onCancel }) {
  const [alt, setAlt] = useState(2);
  const invalid = alt < TAKEOFF_MIN || alt > TAKEOFF_MAX;
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onCancel}>
      <div data-testid="takeoff-dialog" className="w-full max-w-sm mx-4 bg-[#141414] border border-[#F59E0B]/30 rounded-sm p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 text-[#F59E0B]"><ShieldAlert size={18} /><h3 className="font-chivo text-lg font-bold uppercase">Décollage — confirmation renforcée</h3></div>
        <p className="font-mono text-xs text-[#A3A3A3] mt-2">Décoller DRONE-V1 à l'altitude ci-dessous (mode test {TAKEOFF_MIN}–{TAKEOFF_MAX} m).</p>
        <input data-testid="takeoff-altitude" type="number" value={alt} min={TAKEOFF_MIN} max={TAKEOFF_MAX} step={0.5}
          onChange={(e) => setAlt(parseFloat(e.target.value))}
          className={`w-full bg-[#0A0A0A] border ${invalid ? "border-[#EF4444]" : "border-white/15 focus:border-[#F59E0B]"} outline-none rounded-sm px-3 py-2 font-mono text-sm mt-3`} />
        {invalid && <p className="font-mono text-[10px] text-[#EF4444] mt-1.5">Altitude hors bornes de test [{TAKEOFF_MIN}–{TAKEOFF_MAX} m].</p>}
        <p className="font-mono text-sm text-white mt-3">Décoller DRONE-V1 à <span className="text-[#F59E0B] font-bold">{Number.isFinite(alt) ? alt : "?"} m</span> ?</p>
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={onCancel} className="font-mono text-xs uppercase border border-white/15 hover:bg-white/10 rounded-sm px-4 py-2">Annuler</button>
          <button data-testid="takeoff-confirm" disabled={invalid} onClick={() => onConfirm(alt)}
            className="font-mono text-xs uppercase bg-[#F59E0B] hover:bg-[#D97706] disabled:opacity-40 text-black font-bold rounded-sm px-4 py-2">Décoller</button>
        </div>
      </div>
    </div>
  );
}
